import { Logger } from '../../logging/Logger.js';
const LOGGER_CATEGORY = "SceneAssetCache";
/**
 * An enum describing if and how a cached asset should be disposed when it's no longer in use.
 */
export var SceneAssetCachePurgeMode;
(function (SceneAssetCachePurgeMode) {
    /**
     * Disable automatic deleting altogether (historically called persistent load in the API)
     */
    SceneAssetCachePurgeMode[SceneAssetCachePurgeMode["DISABLED"] = 0] = "DISABLED";
    /**
     * Delayed deletion means there is a grace period decided by 'SceneAssetCache.ASSET_CACHE_PURGE_GRACE_PERIOD_SECONDS'
     * during which the asset will not be purged/disposed when being unused.
     * This allows users to delete the last instance of an asset and re-load it within a few frames.
     */
    SceneAssetCachePurgeMode[SceneAssetCachePurgeMode["DELAYED"] = 1] = "DELAYED";
    /**
     * Instant delay means that as soon as the asset is unused, we delete it instantly if it's unused.
     * This is useful for things like custom meshes that a user can't possibly re-create if all of their instances have been removed.
     */
    SceneAssetCachePurgeMode[SceneAssetCachePurgeMode["INSTANT"] = 2] = "INSTANT";
})(SceneAssetCachePurgeMode || (SceneAssetCachePurgeMode = {}));
/**
 * SceneAssetCache takes ownership of SceneAssets to allow caching for easier re-use between SceneAssetInstances.
 * It can also automatically dispose unused SceneAssets, if they've been unused and automatic deletion has been enabled for the asset.
 */
export class SceneAssetCache {
    constructor(scene) {
        this._url_to_asset = new Map();
        this.scene = scene;
    }
    get_debug_information() {
        const assets_information = [];
        this._url_to_asset.forEach((entry) => {
            const info = {
                asset_info: entry.asset.get_debug_information(),
                seconds_since_instances: entry.seconds_since_instances,
                purge_mode: entry.purge_mode
            };
            assets_information.push(info);
        });
        return {
            cache_purge_time: SceneAssetCache.ASSET_CACHE_PURGE_GRACE_PERIOD_SECONDS,
            assets: assets_information
        };
    }
    get_cached_asset(url) {
        if (this._url_to_asset.has(url)) {
            return this._url_to_asset.get(url).asset;
        }
        else {
            return null;
        }
    }
    /**
     * Adds a scene asset to cache, and takes effective ownership of the asset.
     * @param url The 'url' of the asset, this is the key used identifying this asset in the cache.
     * @param asset The asset to cache.
     * @param purge_mode An enum describing which purge behaviour in the cache the asset should follow when no longer in use
     */
    add_to_cache(url, asset, purge_mode) {
        if (this._url_to_asset.has(url)) {
            throw new Error('SceneAssetCache already has URL: ' + url);
        }
        this._url_to_asset.set(url, {
            asset: asset,
            seconds_since_instances: 0.0,
            purge_mode: purge_mode
        });
    }
    set_asset_purge_mode(url, purge_mode) {
        if (this._url_to_asset.has(url)) {
            this._url_to_asset.get(url).purge_mode = purge_mode;
        }
    }
    get_asset_purge_mode(url) {
        if (this._url_to_asset.has(url)) {
            return this._url_to_asset.get(url).purge_mode;
        }
        else {
            return null;
        }
    }
    update_cache(dt) {
        this._url_to_asset.forEach((entry, url) => {
            if (entry.asset.get_instance_count() === 0 && entry.purge_mode !== SceneAssetCachePurgeMode.DISABLED) {
                if (entry.purge_mode === SceneAssetCachePurgeMode.INSTANT || (entry.purge_mode === SceneAssetCachePurgeMode.DELAYED && entry.seconds_since_instances >= SceneAssetCache.ASSET_CACHE_PURGE_GRACE_PERIOD_SECONDS)) {
                    this.unload_from_cache(url);
                }
                else {
                    entry.seconds_since_instances += dt.seconds;
                }
            }
            else {
                entry.seconds_since_instances = 0.0;
            }
        });
    }
    unload_from_cache(url) {
        if (!this._url_to_asset.has(url))
            return false;
        const entry = this._url_to_asset.get(url);
        if (entry.asset.get_instance_count() !== 0) {
            Logger.as(LOGGER_CATEGORY).error('Unloading model in use by one or more object(s), unspecified behaviour may follow. Url of model: ' + url);
        }
        entry.asset.dispose();
        this._url_to_asset.delete(url);
        return true;
    }
    dispose() {
        this._url_to_asset.forEach((entry) => {
            entry.asset.dispose();
        });
        this._url_to_asset.clear();
    }
}
SceneAssetCache.ASSET_CACHE_PURGE_GRACE_PERIOD_SECONDS = 3;
//# sourceMappingURL=SceneAssetCache.js.map